import camelcaseKeys from 'camelcase-keys'
import throttle from 'lodash.throttle'

import {
  type InstructionCheck,
  PROJECT_STATUSES,
  STATEMENT_STATUSES,
  type Statement,
  type StatementDTO,
  type StatementSegment,
} from '@/modules/projects'
import { isStatementToStart } from '@/modules/projects/status'
import {
  executeDelete,
  executeGet,
  executePatch,
  executePost,
} from '@/services/lukita'
import {
  addOrUpdateItems,
  batch,
  getIds,
  removeItems,
  updateItem,
} from '@/utils'
import type {
  ProjectStoreCreator,
  StatementsActions,
  StatementsLoaders,
  StatementsMutations,
} from './types'

export const statementsActions: ProjectStoreCreator<StatementsActions> = (
  set,
  get,
) => ({
  startLoadingStatements: () => set({ loadingStatements: true }),
  stopLoadingStatements: () => set({ loadingStatements: false }),

  // `deep` so nested segments and their reconciliation detail are camelCased too.
  setStatements: (statements) =>
    set({ statements: camelcaseKeys(statements, { deep: true }) }),

  setStatementsStatus: (ids, status) =>
    set((state) => ({
      statements: state.statements.map((statement) => {
        if (ids.includes(statement.id)) {
          return { ...statement, status }
        }
        return statement
      }),
    })),

  addStatements: (statements) =>
    set((state) => ({
      statements: addOrUpdateItems(
        state.statements,
        camelcaseKeys(statements, { deep: true }),
      ),
    })),

  addStatement: (statement) =>
    get().actions.addStatements([statement] as Statement[] | StatementDTO[]),

  setStatement: (statement) =>
    set((state) => ({
      statements: updateItem(
        state.statements,
        camelcaseKeys(statement, { deep: true }),
      ),
    })),

  deleteStatements: (ids) =>
    set((state) => ({
      statements: removeItems(state.statements, ids),
    })),

  removeSegments: (segmentIds) =>
    set((state) => ({
      statements: state.statements.map((statement) =>
        statement.segments?.some((segment) => segmentIds.includes(segment.id))
          ? {
              ...statement,
              segments: statement.segments.filter(
                (segment) => !segmentIds.includes(segment.id),
              ),
            }
          : statement,
      ),
    })),

  mergeSegment: (updated) =>
    set((state) => ({
      statements: state.statements.map((statement) =>
        statement.segments?.some((segment) => segment.id === updated.id)
          ? {
              ...statement,
              segments: statement.segments.map((segment) =>
                segment.id === updated.id ? updated : segment,
              ),
            }
          : statement,
      ),
    })),
})

export const statementsMutations: ProjectStoreCreator<StatementsMutations> = (
  set,
  get,
) => ({
  extractAndEnrichStatements: async (ids) => {
    const project = get().project
    if (!project) return

    const previousStatus = project.status
    const { updateProject, setStatementsStatus } = get().actions
    updateProject({ status: PROJECT_STATUSES.processing })

    const unprocessedIds: number[] = ids
      ? ids
      : get()
          .statements.filter((statement) => isStatementToStart(statement))
          .map(({ id }) => id)

    setStatementsStatus(unprocessedIds, STATEMENT_STATUSES.enqueued)
    const path = `/projects/${project.slug}/extract_and_enrich`
    const body = ids ? { statement_ids: ids } : {}

    try {
      await executePost(path, body)
    } catch (error) {
      setStatementsStatus(unprocessedIds, STATEMENT_STATUSES.notStarted)
      updateProject({ status: previousStatus })
      throw error
    }
  },

  deleteStatements: async (statements) => {
    const project = get().project
    if (!project) return

    const {
      deleteStatements,
      deleteTransactions,
      addStatements,
      addTransactions,
    } = get().actions

    const ids = getIds(statements)
    deleteStatements(ids)

    const transactions = get().transactions.filter(({ statementId }) =>
      ids.includes(statementId),
    )
    deleteTransactions(getIds(transactions))

    try {
      await executeDelete(`/projects/${project.slug}/statements`, { ids })
    } catch (error) {
      addStatements(statements)
      addTransactions(transactions)
      throw error
    }
  },

  createStatements: async (blobs) => {
    set({ mutating: true })
    const project = get().project
    if (!project) return

    const hasFiles = blobs.length > 0
    if (hasFiles) {
      const blobsIds = blobs.map(({ signed_id }) => signed_id)
      const batches = batch(blobsIds, 5)

      const path = `/projects/${project.slug}/statements`
      const requests = batches.map(async (blobs_ids) => {
        const response = await executePost(path, { blobs_ids })
        return response.json()
      })

      try {
        const responses = await Promise.all(requests)
        get().actions.addStatements(responses.flat())
      } catch (error) {
        set({ mutating: false })
        throw error
      }
    }
    set({ mutating: false })
  },

  // Correct a cycle account's printed begin/end balance. lukita recomputes the gap and returns the
  // updated cycle; merge it now so the UI refreshes without waiting on the websocket re-broadcast.
  adjustReconciliationAnchor: async (segmentId, body) => {
    const project = get().project
    if (!project) return

    const response = await executePatch(
      `/projects/${project.slug}/statements/segments/${segmentId}/reconciliation`,
      body,
    )

    get().actions.mergeSegment(
      camelcaseKeys(await response.json(), { deep: true }) as StatementSegment,
    )
  },

  // Accept one cycle as reconciled by hand (or revert it). The engine can't balance some statements
  // (e.g. a Cash App pass-through), so the user signs off. Merge the returned cycle to flip the badge.
  setSegmentReconciled: async (segmentId, reconciled) => {
    const project = get().project
    if (!project) return

    const response = await executePatch(
      `/projects/${project.slug}/statements/segments/${segmentId}/reconciliation/override`,
      { reconciled },
    )

    get().actions.mergeSegment(
      camelcaseKeys(await response.json(), { deep: true }) as StatementSegment,
    )
  },

  // Accept (or revert) a whole statement at once from the overview row. lukita marks every cycle the
  // engine could not balance and returns the refreshed statement (with its cycles).
  setStatementReconciled: async (statementId, reconciled) => {
    const project = get().project
    if (!project) return

    const response = await executePatch(
      `/projects/${project.slug}/statements/${statementId}/reconciliation/override`,
      { reconciled },
    )

    const updatedStatement = camelcaseKeys(await response.json(), {
      deep: true,
    }) as Statement
    get().actions.setStatement(updatedStatement)
  },

  // Permanently delete the duplicate cycles the user discarded from the Repeated Pages review. Drop
  // them from the store now so the page updates without a refetch. Restore the prior list on failure.
  deleteDuplicateSegments: async (segmentIds) => {
    const project = get().project
    if (!project) return

    const previousStatements = get().statements
    get().actions.removeSegments(segmentIds)

    try {
      await executeDelete(`/projects/${project.slug}/statements/segments`, {
        ids: segmentIds,
      })
    } catch (error) {
      set({ statements: previousStatements })
      throw error
    }
  },

  // Cut one upload into cycles again. Every cycle of the file goes in one request, so a page no
  // range covers is left out of every statement.
  resegmentStatement: async (statementId, segments) => {
    const project = get().project
    if (!project) return

    const response = await executePost(
      `/projects/${project.slug}/statements/${statementId}/resegment`,
      { segments },
    )
    get().actions.setStatement(
      camelcaseKeys(await response.json(), { deep: true }) as Statement,
    )
  },

  // Reprocess a single failed cycle. Flip it to enqueued right away so the row shows "Enqueued"; the
  // websocket broadcast lands the real status.
  retryStatementSegment: async (segmentId) => {
    const project = get().project
    if (!project) return

    const setSegmentFields = (fields: Partial<StatementSegment>) =>
      set((state) => ({
        statements: state.statements.map((statement) =>
          statement.segments?.some((segment) => segment.id === segmentId)
            ? {
                ...statement,
                segments: statement.segments.map((segment) =>
                  segment.id === segmentId
                    ? { ...segment, ...fields }
                    : segment,
                ),
              }
            : statement,
        ),
      }))

    const previousSegment = get()
      .statements.flatMap((statement) => statement.segments ?? [])
      .find((segment) => segment.id === segmentId)
    setSegmentFields({ status: STATEMENT_STATUSES.enqueued })

    try {
      await executePost(
        `/projects/${project.slug}/statements/segments/${segmentId}/retry`,
        {},
      )
    } catch (error) {
      // Revert only this cycle's flipped fields, against the store as it is now, so websocket
      // updates that landed for other rows in the meantime are kept.
      if (previousSegment) {
        setSegmentFields({
          status: previousSegment.status,
          failureReason: previousSegment.failureReason,
        })
      }
      throw error
    }
  },

  checkInstruction: async (segmentId, instruction) => {
    const project = get().project
    if (!project) throw new Error('No project loaded')

    const response = await executePost(
      `/projects/${project.slug}/statements/segments/${segmentId}/instruction_check`,
      { instruction },
    )
    return (await response.json()) as InstructionCheck
  },

  reExtractSegment: async (segmentId, instruction, scope) => {
    const project = get().project
    if (!project) return

    const response = await executePost(
      `/projects/${project.slug}/statements/segments/${segmentId}/re_extract`,
      { instruction, scope },
    )
    get().actions.mergeSegment(
      camelcaseKeys(await response.json(), { deep: true }) as StatementSegment,
    )
  },

  insertRows: async (segmentId, instruction, rows) => {
    const project = get().project
    if (!project) return

    const response = await executePost(
      `/projects/${project.slug}/statements/segments/${segmentId}/insert_rows`,
      { instruction, rows },
    )
    get().actions.mergeSegment(
      camelcaseKeys(await response.json(), { deep: true }) as StatementSegment,
    )
  },

  undoInstruction: async (segmentId) => {
    const project = get().project
    if (!project) return

    const response = await executeDelete(
      `/projects/${project.slug}/statements/segments/${segmentId}/instruction`,
    )
    get().actions.mergeSegment(
      camelcaseKeys(await response.json(), { deep: true }) as StatementSegment,
    )
  },
})

export const statementsLoaders: ProjectStoreCreator<StatementsLoaders> = (
  _set,
  get,
) => ({
  fetchStatements: throttle(async (slug: string) => {
    const currentProject = get().project
    if (!currentProject || currentProject.slug !== slug) {
      get().actions.startLoadingStatements()
    }

    const response = await executeGet(`/projects/${slug}/statements`)
    const statements = await response.json()

    get().actions.setStatements(statements)
    get().actions.stopLoadingStatements()
  }, 500),
})
